import React from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { FiUsers, FiBriefcase, FiAlertTriangle, FiTool, FiDollarSign, FiMail, FiDownload } from 'react-icons/fi';
import {
  exportBusinessesCsv, exportPaymentsCsv, exportUsersCsv,
  formatDate, formatMoney, isWithinDays, isPaid, setupProgress, toDate, userName,
} from './adminUtils';

const newestFirst = (a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0);

export default function AdminOverview() {
  const { businesses, users, payments, usersById, businessesById } = useOutletContext();

  const activeBusinesses = businesses.filter(b => !b.isArchived);
  const awaitingVerification = activeBusinesses.filter(b => !b.isVerified);
  const incompleteSetup = activeBusinesses.filter(b => !setupProgress(b, usersById[b.ownerId]).complete);
  const paid = payments.filter(isPaid);
  const admins = users.filter(u => u.isAdmin);

  const stats = [
    { icon: <FiUsers />, label: 'Users', value: users.length, sub: `${users.filter(u => isWithinDays(u.createdAt, 7)).length} new this week`, to: '/admin/users' },
    { icon: <FiBriefcase />, label: 'Businesses', value: activeBusinesses.length, sub: `${activeBusinesses.filter(b => isWithinDays(b.createdAt, 7)).length} new this week`, to: '/admin/businesses' },
    { icon: <FiAlertTriangle />, label: 'Awaiting verification', value: awaitingVerification.length, to: '/admin/businesses' },
    { icon: <FiTool />, label: 'Setup incomplete', value: incompleteSetup.length, to: '/admin/businesses' },
    { icon: <FiDollarSign />, label: 'Paid volume', value: formatMoney(paid.reduce((s, p) => s + p.amount, 0)), sub: `${paid.length} payments`, to: '/admin/payments' },
    { icon: <FiDollarSign />, label: 'Platform fees', value: formatMoney(paid.reduce((s, p) => s + p.platformFee, 0)), to: '/admin/payments' },
  ];

  const recentUsers = [...users].sort(newestFirst).slice(0, 8);
  const recentBusinesses = [...businesses].sort(newestFirst).slice(0, 8);

  return (
    <div className="admin-overview">
      <div className="admin-stat-grid">
        {stats.map(stat => (
          <Link key={stat.label} to={stat.to} className="admin-stat-card">
            <span className="admin-stat-icon">{stat.icon}</span>
            <span className="admin-stat-label">{stat.label}</span>
            <span className="admin-stat-value">{stat.value}</span>
            {stat.sub && <span className="admin-stat-sub">{stat.sub}</span>}
          </Link>
        ))}
      </div>

      <section className="admin-panel">
        <h2>Reports</h2>
        <p className="admin-muted admin-reports-hint">Download everything as CSV (opens in Excel or Google Sheets). Each tab also exports just its filtered view.</p>
        <div className="admin-row-actions">
          <button type="button" className="icon-text-btn" onClick={() => exportUsersCsv(users, businesses)}><FiDownload /> All users ({users.length})</button>
          <button type="button" className="icon-text-btn" onClick={() => exportBusinessesCsv(businesses, usersById)}><FiDownload /> All businesses ({businesses.length})</button>
          <button type="button" className="icon-text-btn" onClick={() => exportPaymentsCsv(payments, businessesById, usersById)}><FiDownload /> All transactions ({payments.length})</button>
        </div>
      </section>

      <div className="admin-panel admin-notice">
        <FiMail />
        <span>
          Email alerts for new sign-ups and new businesses go to all admins
          {admins.length > 0 && <>: <strong>{admins.map(a => a.email).filter(Boolean).join(', ')}</strong></>}.
        </span>
      </div>

      <div className="admin-two-col">
        <section className="admin-panel">
          <h2>Recent sign-ups</h2>
          {recentUsers.length === 0 ? <p className="admin-muted">No users yet.</p> : (
            <ul className="admin-simple-list">
              {recentUsers.map(u => (
                <li key={u.id}>
                  <div>
                    <strong>{userName(u)}</strong>
                    <span className="admin-muted">{u.email}</span>
                  </div>
                  <span className="admin-muted">{formatDate(u.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="admin-panel">
          <h2>New businesses</h2>
          {recentBusinesses.length === 0 ? <p className="admin-muted">No businesses yet.</p> : (
            <ul className="admin-simple-list">
              {recentBusinesses.map(b => {
                const progress = setupProgress(b, usersById[b.ownerId]);
                return (
                  <li key={b.id}>
                    <div>
                      <strong>{b.businessName}</strong>
                      <span className="admin-muted">
                        {formatDate(b.createdAt)} · setup {progress.done}/{progress.total}
                        {!b.isVerified && ' · unverified'}
                      </span>
                    </div>
                    <Link to={`/edit-business/${b.id}`} className="icon-text-btn">Edit</Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
